<script setup lang="ts">
import { DIMENSION_IDS } from '@/constants'
import bar from '@/charts/world/bar'
import bubble from '@/charts/world/bubble'
import map from '@/charts/world/map'
import scatter from '@/charts/world/scatter'
import treemap from '@/charts/world/treemap'

/**
 * Use framework.
 */
const { find, isPrevalence } = useFramework()

/**
 * Use the query params.
 */
const { config, options } = useParams()

/**
 * Current dimension.
 */
const dimension = computed(() => {
    return find(config.dimension)
})

/**
 * Current compare dimension.
 */
const compareDimension = computed(() => {
    return find(config.compareDimension)
})

/**
 * Reactive page title.
 */
const title = computed(() => {
    return [
        unref(dimension)?.name,
        config.viewMode === 'treemap' ? '' : unref(compareDimension)?.name
    ]
        .filter(Boolean)
        .join(' & ')
})

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Container chart element.
 */
const container = shallowRef<HTMLDivElement>()

/**
 * Dimension values.
 */
const data = reactive(useValues(dimension, true))

/**
 * Comparison values.
 */
const comparison = reactive(useValues(compareDimension, true))

/**
 * Check for the device type.
 */
const { isMobile } = useDeviceType()

/**
 * List of pieces for activity status.
 */
const activityPieces = computed(() => {
    return options.activityStatuses
})

/**
 * List of pieces for indicator status.
 */
const indicatorPieces = computed(() => {
    return options.indicatorStatuses.map(status => {
        return data.dimension?.negative
            ? { ...status, color: status.negativeColor }
            : status
    })
})

/**
 * ECharts instance.
 */
const { instance } = useChart(container, () => {
    if (data.dimension?.id === DIMENSION_IDS.GDP_CORRELATION) {
        return scatter(data, comparison)
    }

    if (data.dimension?.type === 'activity') {
        const pieces = unref(activityPieces).filter(piece => {
            return config.activityStatus?.includes(piece.value)
        })

        return config.viewMode === 'map' || unref(isMobile)
            ? map(data, pieces)
            : bubble(data, pieces)
    }

    if (data.dimension?.type === 'indicator') {
        const pieces = unref(indicatorPieces).filter(piece => {
            return config.indicatorStatus?.includes(piece.value)
        })

        return config.viewMode === 'map' || unref(isMobile)
            ? map(data, pieces)
            : bubble(data, pieces)
    }

    if (
        isPrevalence(data.dimension) &&
        data.isAbsolute &&
        config.viewMode !== 'bar'
    ) {
        return treemap(data)
    }

    return bar(data, comparison)
})

/**
 * Set ECharts loading state.
 */
function setLoading() {
    if (data.loading || comparison.loading) {
        instance.value?.showLoading()
    } else {
        instance.value?.hideLoading()
    }
}

/**
 * Update loading status of ECharts instance.
 */
watchEffect(setLoading)

/**
 * Update title based on the current dimension.
 */
useTitle(title, {
    titleTemplate(title) {
        return title ? `${title} | ${t('title')}` : t('title')
    }
})
</script>

<template>
    <UiContainer
        class="grid flex-1 grid-cols-[1fr_max-content] grid-rows-[min-content_1fr_min-content] gap-1 overflow-hidden xl:gap-2 xl:screen-ratio-[2:1]:gap-1"
    >
        <template v-if="dimension">
            <UiHeading
                class="row-start-1"
                :title="title"
                :description="dimension?.description"
                :new-tag="Boolean(dimension?.new)"
                :negative-tag="Boolean(dimension?.negative)"
            />

            <div ref="container" class="col-span-full overflow-hidden" />

            <WorldDataLegend
                :dimension="dimension"
                class="max-xl:pt-4 xl:row-start-1"
                :activity-pieces="activityPieces"
                :indicator-pieces="indicatorPieces"
            />

            <p
                v-if="Boolean(dimension?.negative)"
                class="flex-shrink-0 text-sm italic"
            >
                *{{ t('negative-indicators-disclaimer') }}
            </p>
        </template>

        <UiLoader v-else class="row-span-full" />
    </UiContainer>

    <WorldDataToolbar
        :dimension="dimension"
        :instance="instance"
        :title="title"
        :data="data"
    />
</template>